import { TRPCClientError, TRPCLink } from '@trpc/client';
import { AnyTRPCRouter } from '@trpc/server';
import { observable } from '@trpc/server/observable';

const SERVER_DOWN_MESSAGE = 'Cannot connect to server';


// Wraps a tRPC link to catch low-level transport failures (e.g. fetch() throwing when the
// server is unreachable). Converts those into a clean TRPCClientError with a friendly message,
// while letting all real tRPC / application / auth errors pass through unchanged.
export function withGuard<TRouter extends AnyTRPCRouter>(
    makeLink: () => TRPCLink<TRouter>
): TRPCLink<TRouter> {
    return (runtime) => {
        const inner = makeLink()(runtime);

        return (op) =>
            observable((observer) => {
                const subscription = inner(op).subscribe({
                    next: (value) => observer.next(value),
                    complete: () => observer.complete(),
                    error: (error) => {
                        const mapped = mapTransportError<TRouter>(error);
                        observer.error(mapped ?? error);
                    },
                });

                return () => subscription.unsubscribe();
            });
    };
}

// --- Error mapping ---

function mapTransportError<TRouter extends AnyTRPCRouter>(
    error: unknown
): TRPCClientError<TRouter> | null {
    // Network failure (server down, CORS, dev server off, etc.)
    if (error instanceof TypeError || (error as any)?.cause instanceof TypeError) {
        return new TRPCClientError<TRouter>(SERVER_DOWN_MESSAGE);
    }

    // Invalid or non-JSON backend response (nginx 502, etc.)
    if (error instanceof TRPCClientError && isTransformFailure(error)) {
        return new TRPCClientError<TRouter>(SERVER_DOWN_MESSAGE);
    }

    return null;
}

function isTransformFailure(error: TRPCClientError<AnyTRPCRouter>): boolean {
    const msg = error.message.toLowerCase();
    return (
        msg.includes('unable to transform response') ||
        msg.includes('unexpected end of json') ||
        error.cause instanceof SyntaxError
    );
}